import { DocumentUpload, Trash, Document, GalleryRemove } from 'iconsax-react';
import { ChangeEvent, useState } from 'react';
import { XCircle, Image as ImageIcon } from 'react-feather';
import { toast } from 'react-toastify';
import { ToastMsg } from '../toast/toast-message';
import { ExcelFile, Pdf, TextFile, UploadLoader, Word } from '@/icons/iconsComponent';
import { AxiosProgressEvent } from 'axios';
import { Label } from '../label/label';
import { motion, AnimatePresence } from 'framer-motion';

export interface FileItem {
  uid: string;
  name: string;
  status: 'uploading' | 'done' | 'error' | 'removed';
  url?: string;
  response?: any;
  error?: any;
  percent?: number;
  originFileObj?: File;
  size: number;
  type: string;
}

// Check file type - Updated accepted formats as per requirements
export const acceptedExtensions = [
  '.jpg',
  '.jpeg',
  '.png',
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.txt',
  '.heic',
  '.tiff',
];

export interface FileUploaderProps {
  fileList: FileItem[];
  onChange: (fileList: FileItem[]) => void;
  maxCount?: number;
  multiple?: boolean;
  accept?: string;
  disabled?: boolean;
  uploadFn?: (file: File, onProgress?: (progressEvent: AxiosProgressEvent) => void) => Promise<any>;
  onPreview?: (file: FileItem) => void;
  onRemove?: (file: FileItem) => void;
  beforeUpload?: (file: File, fileList: File[]) => boolean | Promise<boolean>;
  maxSize?: number; // in MB
  uploaderTitle?: string;
}

const FileUploader = ({
  fileList,
  onChange,
  maxCount = 5,
  multiple = true,
  accept = '.jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx,.txt,.heic,.tiff',
  disabled = false,
  uploadFn,
  onPreview,
  onRemove,
  beforeUpload,
  maxSize = 3,
  uploaderTitle = 'انتخاب فایل',
}: FileUploaderProps) => {
  const [dragOver, setDragOver] = useState(false);

  const generateUID = () => {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  };

  const getFileIcon = (fileType: string, fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();

    if (
      fileType.startsWith('image/') ||
      ['jpg', 'jpeg', 'png', 'heic', 'tiff'].includes(extension || '')
    ) {
      return <ImageIcon size={32} className='text-blue-1' />;
    }

    if (extension === 'pdf') {
      return <Pdf />;
    }

    if (['xls', 'xlsx'].includes(extension || '')) {
      return <ExcelFile />;
    }

    if (['doc', 'docx'].includes(extension || '')) {
      return <Word />;
    }

    if (extension === 'txt') {
      return <TextFile />;
    }

    return <Document size={20} className='text-gray-500' />;
  };

  const validateFile = (file: File): boolean => {
    // Check file size
    if (maxSize && file.size > maxSize * 1024 * 1024) {
      toast.error(<ToastMsg message={`حجم فایل نباید بیشتر از ${maxSize} مگابایت باشد`} />, {
        icon: <XCircle className='text-red-1' />,
        progressStyle: { backgroundColor: '#f7525f', height: '2px' },
        style: { padding: '15px' },
        closeButton: false,
      });
      return false;
    }

    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

    if (!acceptedExtensions.includes(fileExtension)) {
      toast.error(
        <ToastMsg message='فرمت‌های مجاز: JPG, JPEG, PNG, PDF, DOC, DOCX, XLS, XLSX, TXT, HEIC, TIFF' />,
        {
          icon: <XCircle className='text-red-1' />,
          progressStyle: { backgroundColor: '#f7525f', height: '2px' },
          style: { padding: '15px' },
          closeButton: false,
        }
      );
      return false;
    }

    return true;
  };

  const handleFileUpload = async (file: File) => {
    const uid = generateUID();
    const newFile: FileItem = {
      uid,
      name: file.name,
      status: 'uploading',
      percent: 0,
      originFileObj: file,
      size: file.size,
      type: file.type,
    };

    // Add file to list with uploading status
    const updatedFileList = [...fileList, newFile];
    onChange(updatedFileList);

    try {
      if (uploadFn) {
        const response = await uploadFn(file, (progressEvent) => {
          if (progressEvent.total && progressEvent.loaded) {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            const updatedFiles = updatedFileList.map((f) =>
              f.uid === uid ? { ...f, percent } : f
            );
            onChange(updatedFiles);
          }
        });

        // Update file status to done
        const finalFiles = updatedFileList.map((f) =>
          f.uid === uid
            ? { ...f, status: 'done' as const, percent: 100, response, url: response?.url }
            : f
        );
        onChange(finalFiles);
      } else {
        // If no upload function, just mark as done with local URL
        const url = URL.createObjectURL(file);
        const finalFiles = updatedFileList.map((f) =>
          f.uid === uid ? { ...f, status: 'done' as const, percent: 100, url } : f
        );
        onChange(finalFiles);
      }
    } catch (error) {
      // Update file status to error
      const errorFiles = updatedFileList.map((f) =>
        f.uid === uid ? { ...f, status: 'error' as const, error } : f
      );
      onChange(errorFiles);

      toast.error(<ToastMsg message='خطا در بارگذاری فایل' />, {
        icon: <XCircle className='text-red-1' />,
        progressStyle: { backgroundColor: '#f7525f', height: '2px' },
        style: { padding: '15px' },
        closeButton: false,
      });
    }
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    // Check max count
    if (maxCount && fileList.length + files.length > maxCount) {
      toast.error(<ToastMsg message={`حداکثر ${maxCount} فایل قابل بارگذاری است`} />, {
        icon: <XCircle className='text-red-1' />,
        progressStyle: { backgroundColor: '#f7525f', height: '2px' },
        style: { padding: '15px' },
        closeButton: false,
      });
      return;
    }

    for (const file of files) {
      if (!validateFile(file)) continue;

      let canUpload = true;
      if (beforeUpload) {
        canUpload = await beforeUpload(file, files);
      }

      if (canUpload) {
        await handleFileUpload(file);
      }
    }

    // Reset input
    e.target.value = '';
  };

  const handleRemove = (file: FileItem) => {
    if (onRemove) {
      onRemove(file);
    }
    const updatedFiles = fileList.filter((f) => f.uid !== file.uid);
    onChange(updatedFiles);
  };

  const handlePreview = (file: FileItem) => {
    if (onPreview) {
      onPreview(file);
      return;
    }

    let fileURL = file.url;

    if (!fileURL && file.originFileObj) {
      fileURL = URL.createObjectURL(file.originFileObj);
    }

    if (fileURL) {
      const link = document.createElement('a');
      link.href = fileURL;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      if (!file.url && file.originFileObj) {
        setTimeout(() => {
          URL.revokeObjectURL(fileURL!);
        }, 1000);
      }
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);

    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    if (!files.length) return;

    // Check max count
    if (maxCount && fileList.length + files.length > maxCount) {
      toast.error(<ToastMsg message={`حداکثر ${maxCount} فایل قابل بارگزاری است`} />, {
        icon: <XCircle className='text-red-1' />,
        progressStyle: { backgroundColor: '#f7525f', height: '2px' },
        style: { padding: '15px' },
        closeButton: false,
      });
      return;
    }

    for (const file of files) {
      if (!validateFile(file)) continue;

      let canUpload = true;
      if (beforeUpload) {
        canUpload = await beforeUpload(file, files);
      }

      if (canUpload) {
        await handleFileUpload(file);
      }
    }
  };

  const canUploadMore = !maxCount || fileList.length < maxCount;

  return (
    <div className='flex flex-col gap-2'>
      {/* Upload Area */}
      <motion.div
        initial={{ opacity: 0.8 }}
        animate={{ opacity: 1 }}
        className={`border-2 border-dashed rounded-lg hover:bg-hover transition-colors ${
          dragOver ? 'border-blue-1 bg-blue-50' : 'border-disableText'
        } ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <label
          className={`flex flex-col items-center justify-center p-3 text-center gap-2 ${
            disabled ? 'cursor-not-allowed' : 'cursor-pointer'
          }`}
        >
          <DocumentUpload size={24} className='text-blue-1' />
          <div className='text-blue-1 body-14-medium'>{uploaderTitle}</div>
          <input
            data-testid='fileuploader'
            type='file'
            className='hidden'
            multiple={multiple}
            accept={accept}
            onChange={handleFileChange}
            disabled={disabled}
          />
        </label>
      </motion.div>
      {/* File List */}
      {fileList.length > 0 && (
        <div className='flex flex-col gap-2'>
          <AnimatePresence>
            {fileList.map((file) => (
              <motion.div
                key={file.uid}
                initial={{ opacity: 0, y: -20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                transition={{
                  duration: 0.3,
                  ease: 'easeOut',
                }}
                className='flex items-center justify-between p-3 border border-border rounded-lg bg-background h-[77px]'
              >
                {/* Loading UI */}
                {file.status === 'uploading' ? (
                  <div className='flex items-center gap-3 flex-1 min-w-0'>
                    <div className='flex items-center justify-center gap-2 flex-1 min-w-0'>
                      <div className='flex items-center justify-center'>
                        <UploadLoader className='scale-[0.6] -m-[12px]' />
                      </div>

                      <div className='flex flex-col items-center gap-1 min-w-[160px] w-full'>
                        <span className='body-14-regular text-purple'>{file.percent ?? 0}%</span>

                        <div className='relative w-full h-1 bg-gray-200 overflow-hidden rounded-xl'>
                          <motion.div
                            className='absolute left-0 top-0 h-full bg-purple'
                            initial={{ width: '0%' }}
                            animate={{ width: `${file.percent ?? 0}%` }}
                            transition={{ duration: 0.3, ease: 'easeInOut' }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ) : file.status === 'error' ? (
                  <>
                    <div className='flex items-center gap-3 flex-1 min-w-0'>
                      <motion.div
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ delay: 0.2, duration: 0.4, ease: 'easeOut' }}
                      >
                        <GalleryRemove size={32} className='text-red-1' />
                      </motion.div>

                      <div className='flex-1 min-w-0'>
                        <div className='flex items-center gap-2'>
                          <Label className='body-14-medium text-red-1 truncate'>
                            خطا در بارگذاری
                          </Label>
                        </div>
                      </div>
                    </div>

                    <div className='flex items-center gap-1'>
                      {!disabled && (
                        <motion.button
                          type='button'
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => handleRemove(file)}
                          className='p-1 rounded hover:bg-hover text-red-1'
                          title='حذف'
                        >
                          <Trash size={18} />
                        </motion.button>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className='flex items-center gap-3 flex-1 min-w-0'>
                      <motion.div
                        initial={{ scale: 0, rotate: -90 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ delay: 0.1, duration: 0.3, ease: 'easeOut' }}
                      >
                        {getFileIcon(file.type, file.name)}
                      </motion.div>

                      <div className='flex-1 min-w-0'>
                        <div className='flex items-center gap-2'>
                          <motion.div
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.2, duration: 0.3 }}
                          >
                            <Label
                              className='body-14-medium text-text truncate hover:text-blue-1-hover cursor-pointer'
                              onClick={() => handlePreview(file)}
                            >
                              {file.name.length > 20 ? `${file.name.slice(0, 30)}...` : file.name}
                            </Label>
                          </motion.div>
                        </div>
                      </div>
                    </div>

                    <div className='flex items-center gap-1'>
                      {!disabled && (
                        <motion.button
                          type='button'
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => handleRemove(file)}
                          className='p-1 rounded hover:bg-hover text-red-1'
                          title='حذف'
                        >
                          <Trash size={18} />
                        </motion.button>
                      )}
                    </div>
                  </>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

export default FileUploader;
